import { LandingNavbar, LandingFooter } from "@/components/landing";
import { useCal } from "@/hooks/useCal";
import {
  ArrowRight,
  MapPin,
  TrendingUp,
  ShoppingBag,
  ShieldCheck,
  AlertTriangle,
  Package,
  Sparkles,
  Scale,
  Target,
} from "lucide-react";

// --- ReflowAI Design System (Strict Blue/Sapphire Theme) ---
// Reusing exact tokens for consistency
const COLORS = {
  brilliance: "#FCFDFD", // Main Background
  springtime: "#EDEFF3", // Glass Card Background
  windweaver: "#C6D1D7", // Borders
  thistle: "#9FA0B5", // Accents (Low contrast - use thistleDark for text)
  thistleDark: "#6B7280", // Darker accent for accessible text (WCAG AA compliant)
  sapphire: "#2F7EDA", // Primary Brand Color
  sapphireDark: "#1E5BA8", // Darker sapphire for accessible text
  blackwater: "#555663", // Body Text
  darkText: "#0F172A", // Headings
  danger: "#EF4444", // Red for threats
  dangerDark: "#DC2626", // Darker red for accessible text
  success: "#22C55E", // Green for wins
  successDark: "#16A34A", // Darker green for accessible text
  warning: "#F59E0B", // Amber for alerts
};

// --- Global Styles ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      --font-primary: 'Plus Jakarta Sans', sans-serif;
    }

    body {
      font-family: var(--font-primary);
      color: ${COLORS.blackwater};
      background-color: ${COLORS.brilliance};
      overflow-x: hidden;
    }

    h1, h2, h3, h4, h5, h6 {
      font-family: var(--font-primary);
      color: ${COLORS.darkText};
      letter-spacing: -0.02em;
    }

    /* Halo Gradient Background */
    .halo-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -1;
      pointer-events: none;
      overflow: hidden;
    }

    .halo-gradient {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
    }

    /* Super Rounded Glass Cards */
    .glass-pill {
      background: rgba(252, 253, 253, 0.6); 
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.8);
      box-shadow: 0 10px 40px -10px rgba(47, 126, 218, 0.1);
    }
    
    .glass-card {
      background: linear-gradient(145deg, rgba(255,255,255,0.7) 0%, rgba(237,239,243,0.5) 100%);
      backdrop-filter: blur(24px);
      border: 1px solid ${COLORS.windweaver};
      transition: box-shadow 0.2s ease, border-color 0.2s ease;
    }

    .glass-card:hover {
      background: linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(237,239,243,0.8) 100%);
      border-color: ${COLORS.sapphire};
      box-shadow: 0 16px 32px -16px rgba(47, 126, 218, 0.2);
    }
  `}</style>
);

// --- UI Components ---

const Button = ({
  children,
  variant = "primary",
  className = "",
  icon: Icon,
  ...props
}: any) => {
  const base =
    "inline-flex items-center justify-center px-8 py-4 rounded-full font-bold transition-colors duration-150 text-sm tracking-wide";

  const styles =
    variant === "primary"
      ? {
          backgroundColor: COLORS.sapphire,
          color: "white",
          boxShadow: `0 10px 30px -5px rgba(47, 126, 218, 0.4)`,
        }
      : variant === "outline"
        ? {
            backgroundColor: "rgba(255,255,255,0.5)",
            color: COLORS.blackwater,
            border: `1px solid ${COLORS.windweaver}`,
            backdropFilter: "blur(4px)",
          }
        : variant === "danger"
          ? {
              backgroundColor: "#FEF2F2",
              color: "#EF4444",
              border: "1px solid #FECACA",
            }
          : {};

  return (
    <button
      className={`${base} ${className} hover:opacity-90`}
      style={styles}
      {...props}
    >
      {children}
      {Icon && <Icon className="ml-2 w-4 h-4" />}
    </button>
  );
};

// --- Visual Components ---

const ProductPlaceholder = ({ color, label, image }: any) => (
  <div
    className={`w-24 h-32 sm:w-32 sm:h-40 rounded-xl bg-gradient-to-br ${color} flex flex-col items-center justify-center relative shadow-xl border border-white/20 overflow-hidden flex-shrink-0`}
  >
    {image ? (
      <>
        <img
          src={image}
          alt={label}
          className="w-full h-full object-cover"
          loading="lazy"
          width="198"
          height="198"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
          <div className="text-white font-bold text-xs uppercase tracking-wider text-center">
            {label}
          </div>
        </div>
      </>
    ) : (
      <>
        <div className="absolute top-2 right-2 opacity-50">
          <Package className="w-4 h-4 text-white" />
        </div>
        <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mb-2">
          <ShoppingBag className="w-8 h-8 text-white" />
        </div>
        <div className="text-white font-bold text-xs uppercase tracking-wider">
          {label}
        </div>
        {/* Reflection effect */}
        <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/10 to-transparent rounded-b-xl"></div>
      </>
    )}
  </div>
);

const TrendLine = ({ data, color }: any) => (
  <div className="flex items-end gap-1 h-12 w-24">
    {data.map((val: any, i: any) => (
      <div
        key={i}
        className="w-full rounded-t-sm opacity-80"
        style={{ height: `${val}%`, backgroundColor: color }}
      ></div>
    ))}
  </div>
);

// New Component: Detailed Availability Chart
const AvailabilityChart = () => {
  const data = [
    {
      location: "Indiranagar",
      myStock: 20,
      compStock: 95,
      platform: "Blinkit",
    },
    { location: "Koramangala", myStock: 45, compStock: 90, platform: "Zepto" },
    {
      location: "HSR Layout",
      myStock: 60,
      compStock: 85,
      platform: "Instamart",
    },
    { location: "Whitefield", myStock: 88, compStock: 92, platform: "Blinkit" },
  ];

  return (
    <div className="bg-white border border-[#EDEFF3] rounded-3xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">
          Stock Availability (%)
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 text-xs font-bold text-[#555663]">
            <div className="w-2 h-2 rounded-full bg-[#2F7EDA]"></div> Me
          </div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#555663]">
            <div className="w-2 h-2 rounded-full bg-[#EF4444]"></div> Competitor
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {data.map((item, i) => (
          <div key={i} className="group">
            <div className="flex justify-between text-sm font-bold text-[#0F172A] mb-1">
              <span>
                {item.location}{" "}
                <span className="text-[#6B7280] font-normal text-xs ml-1">
                  ({item.platform})
                </span>
              </span>
            </div>
            <div className="relative h-6 w-full bg-[#EDEFF3] rounded-full overflow-hidden flex">
              {/* Competitor Bar (Background Context) */}
              <div
                className="absolute top-0 left-0 h-full bg-red-100 rounded-full"
                style={{ width: `${item.compStock}%` }}
              ></div>

              {/* My Stock Bar */}
              <div
                className={`relative h-full rounded-full flex items-center justify-end px-2 text-[10px] font-bold text-white transition-colors duration-200 ${item.myStock < 50 ? "bg-[#EF4444]" : "bg-[#2F7EDA]"}`}
                style={{ width: `${item.myStock}%` }}
              >
                {item.myStock}%
              </div>

              {/* Competitor Marker Line */}
              <div
                className="absolute top-0 h-full w-1 bg-[#EF4444] z-10"
                style={{ left: `${item.compStock}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center mt-1">
              <div className="text-xs text-[#DC2626] font-bold">
                {item.myStock < item.compStock
                  ? `Losing by ${item.compStock - item.myStock}%`
                  : ""}
              </div>
              <div className="text-[10px] text-[#6B7280]">
                Target: {item.compStock}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Specific Module Components ---

const InsightPulse = () => {
  return (
    <div className="glass-pill p-4 pr-6 rounded-[20px] flex items-center gap-4 border border-[#2F7EDA]/30 bg-[#2F7EDA]/5 mb-12 max-w-3xl mx-auto">
      <div className="w-10 h-10 rounded-full bg-[#2F7EDA] flex items-center justify-center text-white shrink-0">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="text-sm md:text-base text-[#0F172A] font-medium leading-snug">
        <span className="font-bold text-[#2F7EDA]">Insight: </span>
        In Delhi NCR, your 1L product is priced{" "}
        <span className="font-bold text-[#DC2626]">8% higher</span> than the
        closest alternative; competitor runs weekend discounts and ranks above
        you—so even when you're in-stock, you lose conversion.
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, subtext, status }: any) => (
  <div className="glass-card p-6 rounded-[32px] flex flex-col items-center text-center">
    <div className="text-xs font-bold text-[#6B7280] uppercase tracking-widest mb-2">
      {label}
    </div>
    <div
      className={`text-4xl font-bold mb-2 ${status === "bad" ? "text-[#DC2626]" : status === "good" ? "text-[#22C55E]" : "text-[#0F172A]"}`}
    >
      {value}
    </div>
    <div className="text-sm font-medium text-[#555663] bg-white/50 px-3 py-1 rounded-full">
      {subtext}
    </div>
  </div>
);

// --- Hero Section ---
const CompetitorHero = () => {
  return (
    <section className="relative pt-44 pb-24 px-4 overflow-hidden flex flex-col items-center">
      <div className="max-w-5xl mx-auto text-center relative z-10">
        <h1 className="text-3xl sm:text-4xl md:text-7xl font-bold text-[#0F172A] leading-[1.1] mb-6 md:mb-8 tracking-tight px-2">
          Who is <span className="text-[#DC2626]">Stealing</span> Your Demand?
        </h1>

        <p className="text-base sm:text-lg md:text-xl text-[#555663] mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
          Don't just track your sales. Track why you lost them. Analyze price
          gaps, promo frequency, and availability advantages of your fiercest
          rivals.
        </p>

        <div>
          <InsightPulse />
        </div>

        {/* The Head-to-Head Visual */}
        <div className="mt-12 relative w-full max-w-5xl mx-auto">
          <div className="glass-card rounded-[24px] md:rounded-[40px] p-1 border border-white/50 shadow-2xl bg-white/40 backdrop-blur-md">
            <div className="bg-[#FCFDFD] rounded-[20px] md:rounded-[36px] overflow-hidden border border-[#EDEFF3] relative min-h-[400px] md:min-h-[500px]">
              {/* Header */}
              <div className="h-12 md:h-16 bg-white border-b border-[#EDEFF3] flex items-center justify-between px-4 md:px-8">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#FEBC2E]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
                </div>
                <div className="text-xs font-bold text-[#6B7280] uppercase tracking-widest flex items-center gap-2">
                  <Target className="w-3 h-3" /> Head-to-Head Analysis
                </div>
              </div>

              {/* Content Grid */}
              <div className="grid md:grid-cols-2 h-full">
                {/* My Brand */}
                <div className="p-4 md:p-8 border-b md:border-b-0 md:border-r border-[#EDEFF3] bg-blue-50/20">
                  <div className="flex items-center gap-4 md:gap-6 mb-6 md:mb-8">
                    {/* Product Visual */}
                    <ProductPlaceholder
                      color="from-[#2F7EDA] to-[#1a5bb8]"
                      label="PureLife"
                      image="/PureOaty-Organic-Oat-Drink.png"
                    />

                    <div>
                      <div className="text-xs font-bold text-[#6B7280] uppercase">
                        My Product
                      </div>
                      <div className="text-xl font-bold text-[#0F172A]">
                        PureLife Oat Milk
                      </div>
                      <div className="text-sm text-[#555663]">
                        1L • Barista Edition
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                        <ShieldCheck className="w-3 h-3" /> Brand Defender
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3] shadow-sm">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-[#555663]">
                          Selling Price
                        </span>
                        <span className="text-sm font-bold text-[#0F172A]">
                          ₹245
                        </span>
                      </div>
                      <div className="w-full bg-[#EDEFF3] h-2 rounded-full overflow-hidden">
                        <div className="bg-[#2F7EDA] h-full w-[80%]"></div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-[10px] text-[#6B7280]">
                          History
                        </span>
                        <TrendLine
                          data={[40, 60, 50, 70, 80]}
                          color="#2F7EDA"
                        />
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3] shadow-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-[#555663]">
                          Availability
                        </span>
                        <span className="text-sm font-bold text-[#DC2626]">
                          82% (OOS Risk)
                        </span>
                      </div>
                      <div className="w-full bg-[#EDEFF3] h-2 rounded-full overflow-hidden">
                        <div className="bg-[#EF4444] h-full w-[82%]"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Competitor */}
                <div className="p-4 md:p-8 bg-red-50/10 relative">
                  {/* VS Badge */}
                  <div className="hidden md:flex absolute top-1/2 -left-6 -translate-y-1/2 w-12 h-12 rounded-full bg-white border border-[#EDEFF3] items-center justify-center text-[#6B7280] font-black text-xs shadow-lg z-10">
                    VS
                  </div>
                  {/* VS Badge Mobile */}
                  <div className="md:hidden flex items-center justify-center -mt-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-white border border-[#EDEFF3] flex items-center justify-center text-[#6B7280] font-black text-xs shadow-lg">
                      VS
                    </div>
                  </div>

                  <div className="flex flex-row-reverse items-center justify-start gap-4 md:gap-6 mb-6 md:mb-8">
                    {/* Product Visual */}
                    <ProductPlaceholder
                      color="from-[#555663] to-[#0F172A]"
                      label="Oatly"
                      image="/oatmilk.png"
                    />

                    <div className="text-right">
                      <div className="text-xs font-bold text-[#DC2626] uppercase">
                        Primary Threat
                      </div>
                      <div className="text-xl font-bold text-[#0F172A]">
                        Oatly Original
                      </div>
                      <div className="text-sm text-[#555663]">
                        1L • Imported
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                        <TrendingUp className="w-3 h-3" /> Aggressive Promo
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3] shadow-sm relative overflow-hidden">
                      <div className="absolute right-0 top-0 bg-[#22C55E] text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg">
                        WINNING
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-[#555663]">
                          Selling Price
                        </span>
                        <span className="text-sm font-bold text-[#22C55E]">
                          ₹225 (-8%)
                        </span>
                      </div>
                      <div className="w-full bg-[#EDEFF3] h-2 rounded-full overflow-hidden">
                        <div className="bg-[#22C55E] h-full w-[60%]"></div>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-[10px] text-[#6B7280]">
                          History
                        </span>
                        <TrendLine
                          data={[80, 75, 70, 65, 60]}
                          color="#22C55E"
                        />
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3] shadow-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-[#555663]">
                          Availability
                        </span>
                        <span className="text-sm font-bold text-[#22C55E]">
                          98% (Reliable)
                        </span>
                      </div>
                      <div className="w-full bg-[#EDEFF3] h-2 rounded-full overflow-hidden">
                        <div className="bg-[#22C55E] h-full w-[98%]"></div>
                      </div>
                    </div>

                    <div className="bg-[#FEF2F2] p-3 rounded-xl border border-[#FECACA] flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-[#DC2626] mt-0.5" />
                      <div className="text-xs text-[#7F1D1D] font-medium">
                        <span className="font-bold">Promotion Alert:</span>{" "}
                        Competitor running "Weekend 20% Off" in 6 clusters
                        including Indiranagar.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Features Grid ---
const CompetitorFeatures = () => {
  return (
    <section className="py-24 px-4 relative">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-16 md:flex justify-between items-end">
          <div className="max-w-2xl">
            <h2 className="text-sm font-bold text-[#2F7EDA] uppercase tracking-widest mb-4">
              Competitive Landscape
            </h2>
            <h3 className="text-4xl font-bold text-[#0F172A] mb-4">
              Decode their Strategy.
            </h3>
            <p className="text-[#555663] text-lg">
              Compare apples to apples (or 200ml to 200ml). See exactly where
              you are losing ground.
            </p>
          </div>
          <div className="hidden md:block">
            <Button variant="outline" icon={ArrowRight}>
              View Full Report
            </Button>
          </div>
        </div>

        {/* Row 1: Metrics + Price & Pack */}
        <div className="grid lg:grid-cols-12 gap-8 mb-8 items-stretch">
          {/* Left Column: Metrics */}
          <div className="lg:col-span-4 flex flex-col justify-between gap-4">
            <MetricCard
              label="Price Index"
              value="108"
              subtext="You are 8% more expensive"
              status="bad"
            />
            <MetricCard
              label="Discount Depth"
              value="15%"
              subtext="Avg. Competitor Discount"
              status="neutral"
            />
            <MetricCard
              label="Availability Win"
              value="-12%"
              subtext="Competitor has better stock"
              status="bad"
            />
          </div>

          {/* Right Column: Price & Pack */}
          <div className="lg:col-span-8 flex">
            <div className="glass-card p-4 sm:p-6 md:p-8 rounded-[24px] md:rounded-[40px] group hover:border-[#2F7EDA] transition-colors w-full flex flex-col">
              <div className="flex items-start justify-between mb-6 md:mb-8">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-[#EDEFF3] flex items-center justify-center text-[#2F7EDA] group-hover:bg-[#2F7EDA] group-hover:text-white transition-colors flex-shrink-0">
                    <Scale className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-bold text-[#0F172A]">
                      Price & Pack Architecture
                    </h4>
                    <p className="text-xs md:text-sm text-[#555663]">
                      Like-for-like comparison (same size/format)
                    </p>
                  </div>
                </div>
                <ArrowRight className="text-[#C6D1D7] group-hover:text-[#2F7EDA] transition-colors hidden sm:block" />
              </div>

              {/* Visual Pack Compare */}
              <div className="grid grid-cols-2 gap-4 mb-8 flex-grow">
                <div className="p-4 bg-white border border-[#EDEFF3] rounded-2xl flex flex-col items-center justify-center">
                  <div className="text-xs font-bold text-[#6B7280] uppercase mb-2">
                    200ml Pouch
                  </div>
                  <div className="flex items-end gap-4 h-24 mb-2">
                    <div
                      className="w-8 bg-[#2F7EDA] rounded-t-lg"
                      style={{ height: "80%" }}
                    ></div>
                    <div
                      className="w-8 bg-[#EDEFF3] rounded-t-lg"
                      style={{ height: "60%" }}
                    ></div>
                  </div>
                  <div className="text-sm font-bold text-[#DC2626]">
                    +14% Gap
                  </div>
                </div>
                <div className="p-4 bg-white border border-[#EDEFF3] rounded-2xl flex flex-col items-center justify-center">
                  <div className="text-xs font-bold text-[#6B7280] uppercase mb-2">
                    1 Litre Pack
                  </div>
                  <div className="flex items-end gap-4 h-24 mb-2">
                    <div
                      className="w-8 bg-[#2F7EDA] rounded-t-lg"
                      style={{ height: "90%" }}
                    ></div>
                    <div
                      className="w-8 bg-[#EDEFF3] rounded-t-lg"
                      style={{ height: "75%" }}
                    ></div>
                  </div>
                  <div className="text-sm font-bold text-[#DC2626]">
                    +8% Gap
                  </div>
                </div>
              </div>

              {/* Mock Table */}
              <div className="w-full overflow-hidden rounded-2xl border border-[#EDEFF3] mt-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-[#EDEFF3] text-[#555663] font-bold">
                    <tr>
                      <th className="p-4">Pack Size</th>
                      <th className="p-4">My Price</th>
                      <th className="p-4">Competitor</th>
                      <th className="p-4">Gap</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    <tr className="border-b border-[#EDEFF3]">
                      <td className="p-4 font-bold text-[#0F172A]">200ml</td>
                      <td className="p-4">₹40</td>
                      <td className="p-4">₹35</td>
                      <td className="p-4 text-[#DC2626] font-bold">+14%</td>
                    </tr>
                    <tr className="border-b border-[#EDEFF3]">
                      <td className="p-4 font-bold text-[#0F172A]">1 Litre</td>
                      <td className="p-4">₹245</td>
                      <td className="p-4">₹225</td>
                      <td className="p-4 text-[#DC2626] font-bold">+8%</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-bold text-[#0F172A]">
                        Combo (2x)
                      </td>
                      <td className="p-4">₹450</td>
                      <td className="p-4">₹460</td>
                      <td className="p-4 text-green-500 font-bold">
                        -2% (Win)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Hyperlocal Price Map + Availability Advantage */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left: Hyperlocal Price Map */}
          <div className="lg:col-span-4">
            <div className="glass-card p-4 sm:p-6 md:p-8 rounded-[24px] md:rounded-[40px] group hover:border-[#9333EA] transition-colors h-full flex flex-col">
              <div className="flex items-center gap-3 md:gap-4 mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-[#EDEFF3] flex items-center justify-center text-[#9333EA] group-hover:bg-[#9333EA] group-hover:text-white transition-colors flex-shrink-0">
                  <TrendingUp className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-[#0F172A]">
                    Hyperlocal Price Map
                  </h4>
                  <p className="text-xs md:text-sm text-[#555663]">
                    City & cluster-wise price variance
                  </p>
                </div>
              </div>

              {/* City Price Cards */}
              <div className="space-y-4 flex-grow">
                {/* City 1 - Delhi */}
                <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#DC2626]"></div>
                      <span className="font-bold text-[#0F172A]">
                        Delhi NCR
                      </span>
                    </div>
                    <span className="text-xs font-medium text-[#DC2626] bg-[#FEE2E2] px-2 py-0.5 rounded-full">
                      +12% vs You
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#555663]">Competitor Avg</span>
                    <span className="font-bold text-[#0F172A]">₹225</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-[#555663]">Your Price</span>
                    <span className="font-bold text-[#DC2626]">₹252</span>
                  </div>
                </div>

                {/* City 2 - Bangalore */}
                <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#22C55E]"></div>
                      <span className="font-bold text-[#0F172A]">
                        Bangalore
                      </span>
                    </div>
                    <span className="text-xs font-medium text-[#22C55E] bg-[#DCFCE7] px-2 py-0.5 rounded-full">
                      -5% vs You
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#555663]">Competitor Avg</span>
                    <span className="font-bold text-[#0F172A]">₹248</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-[#555663]">Your Price</span>
                    <span className="font-bold text-[#22C55E]">₹236</span>
                  </div>
                </div>

                {/* City 3 - Mumbai */}
                <div className="bg-white p-4 rounded-2xl border border-[#EDEFF3]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#F59E0B]"></div>
                      <span className="font-bold text-[#0F172A]">Mumbai</span>
                    </div>
                    <span className="text-xs font-medium text-[#F59E0B] bg-[#FEF3C7] px-2 py-0.5 rounded-full">
                      +3% vs You
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#555663]">Competitor Avg</span>
                    <span className="font-bold text-[#0F172A]">₹230</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-[#555663]">Your Price</span>
                    <span className="font-bold text-[#F59E0B]">₹237</span>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-[#E2E8F0] mt-auto">
                  <div className="text-xs text-[#555663] uppercase tracking-wide mb-2">
                    Price Position Summary
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-center">
                      <div className="text-lg font-bold text-[#DC2626]">2</div>
                      <div className="text-[10px] text-[#555663]">
                        Overpriced
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-[#22C55E]">1</div>
                      <div className="text-[10px] text-[#555663]">Winning</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-[#0F172A]">48</div>
                      <div className="text-[10px] text-[#555663]">
                        Dark Stores
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Availability Advantage */}
          <div className="lg:col-span-8">
            <div className="glass-card p-4 sm:p-6 md:p-8 rounded-[24px] md:rounded-[40px] group hover:border-[#2F7EDA] transition-colors h-full flex flex-col">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-[#EDEFF3] flex items-center justify-center text-[#2F7EDA] group-hover:bg-[#2F7EDA] group-hover:text-white transition-colors flex-shrink-0">
                    <MapPin className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-bold text-[#0F172A]">
                      Availability Advantage
                    </h4>
                    <p className="text-xs md:text-sm text-[#555663]">
                      Availability vs Competitor Stock in High-Priority Zones.
                    </p>
                  </div>
                </div>
                <ArrowRight className="text-[#C6D1D7] group-hover:text-[#2F7EDA] transition-colors hidden sm:block" />
              </div>

              {/* Graph Visualization */}
              <div className="flex-grow flex items-center">
                <div className="w-full">
                  <AvailabilityChart />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Why Competitor Intelligence Matters Section ---
const WhyCompetitorMatters = () => {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto glass-pill rounded-[24px] md:rounded-[48px] p-6 md:p-12 bg-white/40">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
            Why Competitor Intelligence Matters
          </h2>
          <p className="text-[#555663] max-w-2xl mx-auto">
            In Quick Commerce, pricing changes happen hourly. If a competitor
            undercuts you by even 5%, the algorithm shifts demand to them within
            minutes.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: TrendingUp,
              title: "Price War Detection",
              desc: "Competitors run flash sales and price drops constantly. We detect these moves in real-time so you can respond before losing market share.",
            },
            {
              icon: Target,
              title: "Share of Shelf",
              desc: "Know exactly how much digital shelf space you own vs competitors. Track who's winning the Buy Box at each dark store.",
            },
            {
              icon: AlertTriangle,
              title: "Threat Alerts",
              desc: "Get instant notifications when a competitor launches a new SKU, runs a promotion, or undercuts your price in your key territories.",
            },
          ].map((feature, i) => (
            <div
              key={i}
              className="text-center p-6 rounded-3xl hover:bg-white/50 transition-colors"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#FEE2E2] flex items-center justify-center mx-auto mb-6 text-[#DC2626]">
                <feature.icon className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                {feature.title}
              </h3>
              <p className="text-[#555663] leading-relaxed text-sm">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Component ---
const CompetitorCTA = () => {
  const { openCalModal } = useCal();

  return (
    <section className="py-20 px-4 text-center">
      <div className="max-w-4xl mx-auto glass-card rounded-[32px] md:rounded-[60px] p-8 md:p-16 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#EF4444] to-[#F59E0B]"></div>

        <h2 className="text-3xl md:text-5xl font-bold text-[#0F172A] mb-6 md:mb-8">
          Stop Losing to Competitors.
        </h2>
        <p className="text-lg text-[#555663] mb-10 max-w-2xl mx-auto">
          Get the full report on who is stealing your demand, where, and how.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button icon={ArrowRight} onClick={openCalModal}>
            Schedule a Call
          </Button>
          <Button variant="outline" onClick={openCalModal}>
            Book a Demo
          </Button>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Export ---
export function CompetitorLandingPage() {
  return (
    <>
      <GlobalStyles />
      <div className="min-h-screen relative selection:bg-[#2F7EDA] selection:text-white">
        {/* Fixed Halo Background (Modified colors for Danger/Competitor Theme) */}
        <div className="halo-container">
          <div className="halo-gradient w-[800px] h-[800px] bg-[#9FA0B5] top-[-20%] left-[-10%] opacity-20"></div>
          <div className="halo-gradient w-[600px] h-[600px] bg-[#EF4444] bottom-[-10%] right-[-10%] opacity-10"></div>
          <div className="halo-gradient w-[400px] h-[400px] bg-[#2F7EDA] top-[40%] left-[30%] opacity-20"></div>
        </div>

        <LandingNavbar />

        <main>
          <CompetitorHero />

          <CompetitorFeatures />

          {/* Why Competitor Intelligence Matters */}
          <WhyCompetitorMatters />

          {/* CTA Section */}
          <CompetitorCTA />
        </main>

        <LandingFooter />
      </div>
    </>
  );
}
